import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Wordmark } from "../components/Wordmark";
import { toast } from "sonner";
import { Loader2, Check, Sparkles, Mic, BookOpen, Users } from "lucide-react";
import { useCurrency, LOCAL } from "../lib/region";

const perks = [
  { icon: Mic, text: "Speak or type your memories — we turn them into prose" },
  { icon: Sparkles, text: "A gentle AI interviewer that asks one warm question at a time" },
  { icon: BookOpen, text: "Your memories organised into chapters and a finished book" },
  { icon: Users, text: "Invite family to add their own memories to your story" },
];

export default function StartTrial() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const cur = useCurrency();
  const [busy, setBusy] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [promo, setPromo] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  const redeemPromo = async () => {
    if (!promo.trim()) { toast.error("Please enter a code."); return; }
    setPromoBusy(true);
    try {
      await api.post("/promo/redeem", { code: promo.trim() });
      await refreshUser();
      toast.success("Code applied — welcome aboard!");
      navigate("/home", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "That code isn't valid.");
      setPromoBusy(false);
    }
  };

  useEffect(() => {
    if (user?.access?.gate === "ok") navigate("/home", { replace: true });
    else if (user?.access?.gate === "expired") navigate("/subscribe", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/start-trial", { origin_url: window.location.origin });
      // Meta Pixel: the 7-day free trial has been successfully started (fires only here).
      if (window.fbq) window.fbq("track", "StartTrial");
      await refreshUser();
      navigate("/home", { replace: true });
    } catch {
      toast.error("Couldn't start your trial. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain bg-cream flex flex-col" data-testid="start-trial-page">
      <header className="border-b border-line bg-cream/85 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2.5">
          <img src="/logo.png" alt="Write My Story" className="h-9 w-9 object-contain" />
          <Wordmark size="text-lg sm:text-xl" />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-lg bg-white rounded-3xl border border-line p-8 sm:p-10 animate-fade-up">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-sage font-semibold">
            <Sparkles size={14} /> Welcome{user?.profile?.preferred_name ? `, ${user.profile.preferred_name}` : ""}
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mt-3 leading-tight">
            Start your 7-day free trial
          </h1>
          <p className="text-subtle mt-3 leading-relaxed">
            Enjoy everything Write My Story offers — completely free for 7 days.
            <span className="font-semibold text-charcoal"> No card required.</span> We'll only ask
            you to choose a plan once your trial ends.
          </p>

          <ul className="mt-6 space-y-3">
            {perks.map((p) => (
              <li key={p.text} className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-sagelight text-teal flex items-center justify-center shrink-0"><p.icon size={16} /></span>
                <span className="text-charcoal/85 leading-snug mt-1">{p.text}</span>
              </li>
            ))}
          </ul>

          <button data-testid="start-trial-btn" onClick={start} disabled={busy}
            className="mt-8 w-full flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white h-14 rounded-full font-semibold text-lg transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={20} /> : <><Check size={20} /> Start my free trial</>}
          </button>
          <p className="text-center text-xs text-subtle mt-4">7 days free · no card needed · cancel anytime</p>
          {cur !== "GBP" && <p className="text-center text-xs text-sage font-medium mt-2" data-testid="trial-cad-reassure">Prices in {cur === "USD" ? "US$" : "CA$"} for {LOCAL[cur].who} customers</p>}
          <p className="text-center text-xs text-subtle mt-2" data-testid="trial-pricing-note">
            When your trial ends: £7.99/month{cur !== "GBP" ? ` (≈ ${LOCAL[cur].monthly})` : ""} or £69/year{cur !== "GBP" ? ` (≈ ${LOCAL[cur].yearly})` : ""}.
          </p>

          <div className="mt-6 pt-5 border-t border-line text-center">
            {!showPromo ? (
              <button data-testid="have-promo-btn" onClick={() => setShowPromo(true)}
                className="text-teal text-sm font-medium hover:underline">Have an invite code?</button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2 max-w-sm mx-auto">
                <input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value)}
                  placeholder="Enter your code" onKeyDown={(e) => e.key === "Enter" && redeemPromo()}
                  className="flex-1 h-11 px-4 rounded-full border border-line bg-white uppercase text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/20" />
                <button data-testid="promo-redeem-btn" onClick={redeemPromo} disabled={promoBusy}
                  className="bg-teal hover:bg-tealdark text-cream px-5 h-11 rounded-full font-semibold text-sm disabled:opacity-60">
                  {promoBusy ? "Applying…" : "Apply"}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
